import { HTMLAttributes, forwardRef } from "react";

/**
 * Card — Archival Studio primitive.
 *
 * Default: frosted paper-raised surface, 22px radius, soft shadow.
 *
 * Variant `chapter`: adds a 4px walnut spine on the left edge.
 *   The only "left-border accent" allowed in the system, and only
 *   here, because it's literally a book spine.
 *
 * Variant `signature`: adds a 4px paper-3 strip on the right edge to
 *   suggest a folded gathering.
 *
 * Set `interactive` to enable hover lift.
 *
 * Source: project/README.md "Cards", project/preview/components-chapter-card.html
 */

type Variant = "default" | "chapter" | "signature";

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: Variant;
  interactive?: boolean;
}

const BASE =
  "relative rounded-as-3 border border-[color:color-mix(in_oklab,var(--as-paper-raised)_76%,transparent)] bg-paper-raised/90 shadow-as-1 backdrop-blur-2xl " +
  "transition-[box-shadow,background-color,transform] duration-as-2 ease-as-out " +
  "p-[18px_22px]";

const VARIANT: Record<Variant, string> = {
  default:   "",
  chapter:   "pl-[30px] before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[4px] before:bg-walnut",
  signature: "pr-[28px] after:content-[''] after:absolute after:right-0 after:top-0 after:bottom-0 after:w-[4px] after:bg-paper-3",
};

const INTERACTIVE = "hover:bg-paper-raised hover:shadow-as-2 cursor-pointer";

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { variant = "default", interactive = false, className = "", ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={`${BASE} ${VARIANT[variant]} ${interactive ? INTERACTIVE : ""} ${className}`}
      {...rest}
    />
  );
});
